/**
 * The document-tree seed, ported from `OEM+_Platform_Demo.html`.
 *
 * Source lines
 * ------------
 * - `DOCTREE` — demo lines 2408–2410 (13 hand-written folders plus a spread
 *   that generates one folder per extra-connector). Consumed by the tree
 *   builder at demo lines 2411–2417 (`docBtn`, the `.fold` / `.grp` / `.items`
 *   markup and the `#tree-q` filter).
 *
 * The placeholder scheme
 * ----------------------
 * Five of the file labels were template literals interpolating values that only
 * exist once the data pack is loaded. Baking a number or a machine id into the
 * seed would freeze it against one pack, so **every interpolation is a
 * placeholder field instead of a value**:
 *
 * | demo source                                | ported segment                |
 * |--------------------------------------------|-------------------------------|
 * | `` `work_orders.csv — ${WOS.length.toLocaleString()} orders` `` | `{ countOf: 'workOrders' }` |
 * | `` `${IDS.T22}_2026-09-08_DS.jsonl` ``      | `{ machineAlias: 'T22' }`     |
 * | `` `${IDS.L31}_2026-09-08_DS.jsonl` ``      | `{ machineAlias: 'L31' }`     |
 * | `` `${IDS.T22}_can1_2026-09-09_0438-0443.log` `` | `{ machineAlias: 'T22' }` |
 * | `` `drillplan_${IDS.J07}_BR_1840XC_cut_212.xml` `` | `{ machineAlias: 'J07' }` |
 *
 * A label is therefore a {@link DocLabel} — an ordered list of segments. Text
 * segments are copied character for character, including the `—` and the
 * surrounding spaces. The scene lane renders a label by mapping
 * `{ countOf }` through its own dataset sizes (formatted with
 * `toLocaleString()`, as the demo did) and `{ machineAlias }` through the
 * pack's `IDS` map, then joining.
 *
 * What else changed
 * -----------------
 * 1. **Seven folders were not literals at all** — they were `.map()` calls over
 *    the data pack (`B.docs`, `B.emails`, `B.meetings`, `B.videos`,
 *    `B.report_files`) and one spread that grouped `B.extra` by connector.
 *    Those become {@link DocTreeFolder} values of kind `'dataset'` and
 *    `'connectors'`, describing the derivation rather than listing files that
 *    do not exist in this layer.
 *
 * 2. **No markup.** `docBtn` wrapped each entry in
 *    `<button class="doc" data-k data-m><span class="tag-fmt">EXT</span>…`.
 *    That is all render-time: the format tag is just the filename extension
 *    upper-cased, and `data-m` is the machine list used by the filter box.
 *    Neither is data, so neither is carried.
 *
 * 3. **No handler strings.** `docBtn` attached `openDoc(k+fn)` in JavaScript,
 *    not as an `onclick` attribute, but it is still behaviour: a scene builds
 *    the same key from {@link DocTreeFile.prefix} plus the resolved label.
 *
 * Quirk worth knowing
 * -------------------
 * The demo forms its document key by *concatenating* prefix and label, so a
 * prefix such as `'tab:fleet'` yields `'tab:fleetfleet_register.csv'` and
 * `docView` then resolves `SRC['fleetfleet_register.csv']`, which does not
 * exist. Prefixes that end in `':'` (`'wo:'`, `'doc:'`, `'eml:'`…) work; the
 * seven `'tab:<key>'` prefixes do not. This is ported exactly as written —
 * see {@link DocTreeFile.prefix} — rather than silently repaired.
 * TODO(P3): decide whether the scene should key `'tab:'` entries off the
 * prefix suffix (`fleet`, `health`, `cpm`, …) instead of the concatenation.
 *
 * Data Schema layer — pure data and pure types. No React, no side effects.
 */

import type { MachineAlias } from './modules';

/* ------------------------------------------------------------------ *
 * Labels
 * ------------------------------------------------------------------ */

/** Dataset sizes a document label can quote. */
export const DOC_COUNTS = ['workOrders'] as const;

/** Closed set of dataset sizes a document label can quote. */
export type DocCount = (typeof DOC_COUNTS)[number];

/**
 * One piece of a file label.
 *
 * - `{ text }` — literal copy from the demo, verbatim.
 * - `{ countOf }` — the size of a named dataset, rendered `toLocaleString()`.
 * - `{ machineAlias }` — a machine id resolved through the pack's `IDS` map.
 */
export type DocLabelSegment =
  | { readonly text: string }
  | { readonly countOf: DocCount }
  | { readonly machineAlias: MachineAlias };

/** A file label, as an ordered list of segments. */
export type DocLabel = readonly DocLabelSegment[];

/** Literal text segment. */
export const dt = (text: string): DocLabelSegment => ({ text });

/** Dataset-size placeholder. */
export const dcount = (countOf: DocCount): DocLabelSegment => ({ countOf });

/** Machine-id placeholder. */
export const dmachine = (machineAlias: MachineAlias): DocLabelSegment => ({ machineAlias });

/* ------------------------------------------------------------------ *
 * Entries and folders
 * ------------------------------------------------------------------ */

/** One file button in the tree. */
export interface DocTreeFile {
  /**
   * The demo's `k` — the document-kind prefix.
   *
   * Concatenated with the resolved label to form the key `docView` parses.
   * See the "Quirk worth knowing" note in the file header.
   */
  readonly prefix: string;
  /** The label, as printed. */
  readonly label: DocLabel;
}

/**
 * How a generated folder's files are derived from the loaded data pack.
 *
 * `dataset` and the two field names are the demo's own expressions, carried
 * verbatim as opaque strings. Nothing here evaluates them.
 */
export interface DocTreeSource {
  /** e.g. `'B.docs'`, `'B.emails'`, `'B.report_files'`. */
  readonly dataset: string;
  /** `d.folder === <this>`, when the demo filtered. */
  readonly folder?: string;
  /** The field holding the file name, e.g. `'file'`, `'transcript'`. */
  readonly field?: string;
  /** The demo's `||` fallback, e.g. `v.transcript||v.video_id`. */
  readonly fallbackField?: string;
}

/** One top-level folder in the tree. */
export type DocTreeFolder =
  | {
      readonly kind: 'static';
      /** Folder name, verbatim. */
      readonly name: string;
      readonly files: readonly DocTreeFile[];
    }
  | {
      readonly kind: 'dataset';
      /** Folder name, verbatim. */
      readonly name: string;
      /** The document-kind prefix applied to every file in the folder. */
      readonly prefix: string;
      readonly source: DocTreeSource;
    }
  | {
      readonly kind: 'connectors';
      /** The document-kind prefix applied to every file. */
      readonly prefix: string;
      readonly source: DocTreeSource;
      /** How the demo built these folders. */
      readonly note: string;
    };

/**
 * Demo lines 2408–2410 — `const DOCTREE=[…]`.
 *
 * Thirteen literal folders followed by the `B.extra` spread, in demo order.
 */
export const DOCTREE: readonly DocTreeFolder[] = [
  {
    kind: 'static',
    name: 'SAP PM',
    files: [
      {
        prefix: 'wo:',
        label: [dt('work_orders.csv — '), dcount('workOrders'), dt(' orders')],
      },
      { prefix: 'sap:', label: [dt('sap_pm_orders_export.json')] },
    ],
  },
  {
    kind: 'static',
    name: 'Fleet register',
    files: [
      { prefix: 'tab:fleet', label: [dt('fleet_register.csv')] },
      { prefix: 'tab:health', label: [dt('component_health.csv')] },
      { prefix: 'tab:cpm', label: [dt('cost_per_machine.csv')] },
      { prefix: 'tab:energy', label: [dt('energy_by_machine_12d.csv')] },
    ],
  },
  {
    kind: 'static',
    name: 'Pitram · shift-end (KG, BR)',
    files: [
      { prefix: 'xml:', label: [dt('pitram_shift_export_KG_2026-09-03.xml')] },
      { prefix: 'xml:', label: [dt('pitram_shift_export_BR_2026-09-03.xml')] },
      { prefix: 'tab:shift', label: [dt('shift_records_all_mines.csv')] },
      { prefix: 'tab:metric', label: [dt('daily_key_metric_all_machines.csv')] },
      { prefix: 'tab:events', label: [dt('fault_events.csv')] },
    ],
  },
  {
    kind: 'static',
    name: 'Live telematics · MQTT (MI)',
    files: [
      { prefix: 'mqtt:', label: [dmachine('T22'), dt('_2026-09-08_DS.jsonl')] },
      { prefix: 'mqtt:', label: [dmachine('L31'), dt('_2026-09-08_DS.jsonl')] },
      { prefix: 'can:', label: [dmachine('T22'), dt('_can1_2026-09-09_0438-0443.log')] },
      { prefix: 'py:', label: [dt('mi_live_collector.py')] },
    ],
  },
  {
    kind: 'static',
    name: 'Drill plans · IREDES-style',
    files: [
      {
        prefix: 'plan:',
        label: [dt('drillplan_'), dmachine('J07'), dt('_BR_1840XC_cut_212.xml')],
      },
    ],
  },
  {
    kind: 'dataset',
    name: 'SharePoint · OEM library',
    prefix: 'doc:',
    source: { dataset: 'B.docs', folder: '04_sharepoint_library', field: 'file' },
  },
  {
    kind: 'static',
    name: 'Salesforce · parts',
    files: [
      { prefix: 'tab:inv', label: [dt('inventory_snapshot.csv')] },
      { prefix: 'tab:parts', label: [dt('parts_catalogue.csv')] },
      { prefix: 'tab:cases', label: [dt('service_cases.csv')] },
    ],
  },
  {
    kind: 'static',
    name: 'Cost ledger',
    files: [
      { prefix: 'tab:ledger_c', label: [dt('cost_by_crew.csv')] },
      { prefix: 'tab:efac', label: [dt('energy_factors.csv')] },
    ],
  },
  {
    kind: 'dataset',
    name: 'Outlook · email',
    prefix: 'eml:',
    source: { dataset: 'B.emails', field: 'file' },
  },
  {
    kind: 'dataset',
    name: 'Teams · meetings',
    prefix: 'mtg:',
    source: { dataset: 'B.meetings', field: 'transcript' },
  },
  {
    kind: 'dataset',
    name: 'Training & headset video (transcripts)',
    prefix: 'vtt:',
    source: { dataset: 'B.videos', field: 'transcript', fallbackField: 'video_id' },
  },
  {
    kind: 'dataset',
    name: 'Incident records',
    prefix: 'doc:',
    source: { dataset: 'B.docs', folder: '08_incidents', field: 'file' },
  },
  {
    kind: 'dataset',
    name: 'Reports (generated)',
    prefix: 'rep:',
    source: { dataset: 'B.report_files' },
  },
  {
    kind: 'connectors',
    prefix: 'x:',
    source: { dataset: 'B.extra', field: 'file' },
    note:
      'One folder per distinct `e.conn`, named after the connector. Within a folder the demo grouped by `e.group` — but only when a connector has more than one group, in which case each group becomes a nested `grp` node labelled with the group name, or `Files` when the group is empty. A connector with a single group lists its files flat.',
  },
];
